import { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectFoldersRecord, selectRootByMatterId } from '@/user_files/folders/store/selectors'
import { UserFoldersActions } from '@/user_files/folders/store/slice'

type UseMatterFileCountsParams = {
  matterId: string
  /** The folder the drive sync writes into, from the active mapping. Null when nothing is synced. */
  syncedFolderId: string | null
  enabled: boolean
}

export type MatterFileCounts = {
  /** Files under the synced folder. Zero when nothing is synced. */
  syncedFileCount: number
  /** Files in the matter that were not synced in. */
  uploadedFileCount: number
}

/**
 * Synced vs uploaded file counts for a matter, from the recursive `file_count` on the root folder
 * and the synced folder: uploaded = root - synced.
 *
 * `counts` is null until both folders have loaded. The root is refetched on entry since a cached
 * copy may be stale.
 */
export function useMatterFileCounts({ matterId, syncedFolderId, enabled }: UseMatterFileCountsParams) {
  const dispatch = useAppDispatch()
  const rootFolderId = useAppSelector((state) => selectRootByMatterId(state)[matterId])
  const rootFolder = useAppSelector((state) => (rootFolderId ? selectFoldersRecord(state)[rootFolderId]?.data ?? null : null))
  const syncedFolder = useAppSelector((state) => (syncedFolderId ? selectFoldersRecord(state)[syncedFolderId]?.data ?? null : null))

  const refresh = useCallback(() => {
    dispatch(UserFoldersActions.fetchRootFolder({ matterId, force: true }))
    if (syncedFolderId) {
      dispatch(UserFoldersActions.fetchFolderById({ folderId: syncedFolderId }))
    }
  }, [dispatch, matterId, syncedFolderId])

  useEffect(() => {
    if (enabled) refresh()
  }, [enabled, refresh])

  let counts: MatterFileCounts | null = null
  if (enabled && rootFolder && (syncedFolderId == null || syncedFolder)) {
    const syncedFileCount = syncedFolder?.file_count ?? 0
    // The two folders load independently, so the synced count can briefly exceed the root's.
    counts = { syncedFileCount, uploadedFileCount: Math.max(rootFolder.file_count - syncedFileCount, 0) }
  }

  return { counts, refresh }
}
